/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';


/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ConditionalContext from 'src/context/ConditionalContext';
import StaticGridContext from '../../../../context/StaticGridContext';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */
interface IOpmsStaticGridProps {
    headers: IOpmsStaticGridHeader[]
}

interface IOpmsStaticGridStyleProps {
    hidden: boolean;
}


export interface IOpmsStaticGridHeader extends IOpmsStaticGridHeaderStyleProps {
    columnName: string;
    content: React.ReactNode;
}

interface IOpmsStaticGridHeaderStyleProps {
    width?: string | number;
    minWidth?: string | number;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme, IOpmsStaticGridStyleProps>(theme => ({
    container: {
        display: props => props.hidden ? 'none' : 'block',
        opacity: props => props.hidden ? 0 : 1,
        overflowX: 'auto'
    },
    table: {
        minWidth: '100%'
    }
}));

const useHeaderStyles = makeStyles<Theme, IOpmsStaticGridHeaderStyleProps>(theme => ({
    header: {
        width: props => props.width ? props.width : undefined,
        minWidth: props => props.minWidth ? props.minWidth : undefined
    }
}));


/*
 * ---------------------------------------------------------------------------------
 * components
 * ---------------------------------------------------------------------------------
 */

const OpmsStaticHeader: React.FunctionComponent<IOpmsStaticGridHeader> = ({
    content,
    columnName,
    width,
    minWidth,
    children
}) => {
    const classes = useHeaderStyles({ width, minWidth });

    return (
        <TableCell className={classes.header}>
            <strong>
                {content}
            </strong>
        </TableCell>
    )
}

const OpmsStaticGrid: React.FunctionComponent<IOpmsStaticGridProps> = ({
    headers,
    children
}) => {
    const conditionalResult = React.useContext(ConditionalContext)

    const classes = useStyles({ hidden: conditionalResult?.result === false && conditionalResult?.mode === 'Show' });

    return (
        <StaticGridContext.Provider value={headers}>
            <div
                className={classes.container}

            >
                <Table
                    className={classes.table}
                >
                    <TableHead>
                        <TableRow>
                            {
                                headers?.map((header) => {
                                    return (
                                        <OpmsStaticHeader
                                            key={header.columnName}
                                            {...header}
                                        />
                                    );
                                })
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {children}
                    </TableBody>
                </Table>
            </div>
        </StaticGridContext.Provider>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default OpmsStaticGrid;
